import type { SpillEvent } from '@/data/types';

export const EVENT_TRIGGER_SJANSE = 0.2;
export const EVENT_TRIGGER_SJANSE_TIDLIG = 0.4;
export const TIDLIG_SPILL_MAX_DAG = 14;
export const EVENT_COOLDOWN_DAGER = 3;

export function eventTriggerSjanseFor(dag: number): number {
  return dag <= TIDLIG_SPILL_MAX_DAG ? EVENT_TRIGGER_SJANSE_TIDLIG : EVENT_TRIGGER_SJANSE;
}

export type EventContext = {
  dag: number;
  omdomme: number;
  kroner: number;
  antallUtgivelser: number;
  flags: ReadonlyArray<string>;
  seenEvents: ReadonlyArray<string>;
};

const advarteBetingelser = new Set<string>();

export function evaluateTriggers(
  events: ReadonlyArray<SpillEvent>,
  ctx: EventContext,
): SpillEvent[] {
  const ukenu = Math.floor((ctx.dag - 1) / 7) + 1;
  const ut: SpillEvent[] = [];
  for (const e of events) {
    if (ctx.seenEvents.includes(e.id)) continue;
    if (!matcherTrigger(e, ctx, ukenu)) continue;
    ut.push(e);
  }
  return ut;
}

function matcherTrigger(event: SpillEvent, ctx: EventContext, uke: number): boolean {
  const t = event.trigger ?? {};
  if (typeof t.min_uke === 'number' && uke < t.min_uke) return false;
  if (typeof t.maks_uke === 'number' && uke > t.maks_uke) return false;
  if (typeof t.min_omdomme === 'number' && ctx.omdomme < t.min_omdomme) return false;
  if (typeof t.betingelse === 'string' && t.betingelse.trim().length > 0) {
    const utfall = evalBetingelse(t.betingelse, event.id, ctx);
    if (utfall === null) return false;
    if (!utfall) return false;
  }
  return true;
}

function evalBetingelse(
  uttrykk: string,
  eventId: string,
  ctx: EventContext,
): boolean | null {
  const deler = splitOgEller(uttrykk);
  let resultat: boolean | null = null;
  for (const { operator, frase } of deler) {
    const verdi = vurderFrase(frase, ctx);
    if (verdi === null) {
      varselEnGang(eventId, frase);
      return null;
    }
    if (resultat === null) {
      resultat = verdi;
    } else if (operator === 'og') {
      resultat = resultat && verdi;
    } else if (operator === 'eller') {
      resultat = resultat || verdi;
    }
  }
  return resultat;
}

type Frase = { operator: 'start' | 'og' | 'eller'; frase: string };

function splitOgEller(uttrykk: string): Frase[] {
  const tokens: Frase[] = [];
  const regex = /\s+(og|eller)\s+/i;
  let resten = uttrykk.trim();
  let operator: Frase['operator'] = 'start';
  while (resten.length > 0) {
    const treff = regex.exec(resten);
    if (!treff || typeof treff.index !== 'number') {
      tokens.push({ operator, frase: resten.trim() });
      break;
    }
    const venstre = resten.slice(0, treff.index).trim();
    tokens.push({ operator, frase: venstre });
    operator = treff[1].toLowerCase() as Frase['operator'];
    resten = resten.slice(treff.index + treff[0].length);
  }
  return tokens.filter((t) => t.frase.length > 0);
}

function vurderFrase(frase: string, ctx: EventContext): boolean | null {
  const f = frase.trim();

  // antall_utgivelser >= N
  const utg = f.match(/^antall_utgivelser\s*(>=|<=|>|<|=)\s*(\d+)$/);
  if (utg) {
    return sammenlign(ctx.antallUtgivelser, utg[1], Number(utg[2]));
  }

  // kroner > N / kroner < N
  const kr = f.match(/^kroner\s*(>=|<=|>|<|=)\s*(\d+)$/);
  if (kr) {
    return sammenlign(ctx.kroner, kr[1], Number(kr[2]));
  }

  // omdomme >= N
  const om = f.match(/^omdomme\s*(>=|<=|>|<|=)\s*(\d+)$/);
  if (om) {
    return sammenlign(ctx.omdomme, om[1], Number(om[2]));
  }

  // flag X / not flag X
  const flag = f.match(/^(not\s+)?flag\s+(\w+)$/);
  if (flag) {
    const negert = !!flag[1];
    const navn = flag[2];
    const har = ctx.flags.includes(navn);
    return negert ? !har : har;
  }

  // Ukjent uttrykk
  return null;
}

function sammenlign(verdi: number, op: string, mot: number): boolean {
  switch (op) {
    case '>=':
      return verdi >= mot;
    case '<=':
      return verdi <= mot;
    case '>':
      return verdi > mot;
    case '<':
      return verdi < mot;
    case '=':
      return verdi === mot;
    default:
      return false;
  }
}

function varselEnGang(eventId: string, frase: string): void {
  const noekkel = `${eventId}::${frase}`;
  if (advarteBetingelser.has(noekkel)) return;
  advarteBetingelser.add(noekkel);
  console.warn(`Event ${eventId}: kan ikke evaluere betingelse «${frase}» — hopper over`);
}

export function _resetVarslerForTest(): void {
  advarteBetingelser.clear();
}
